import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LandingPage from "./components/LandingPage";
import "./components/LandingPage.css";
import PriceChart from "./components/PriceChart";
import ChatBot from "./components/ChatBot";
import VoiceChat from "./components/VoiceChat";
import MemoryDashboard from "./components/MemoryDashboard";
import StreamingChat from "./components/StreamingChat";
// Heavy panels — lazy-loaded (memory-tier safety: only load when the user opens them)
const W6ShapeCockpit    = lazy(() => import("./components/W6ShapeCockpit"));
const StoreDashboard    = lazy(() => import("./components/StoreDashboard"));
const CockpitDalpha     = lazy(() => import("./components/CockpitDalpha"));
const ContentStudio     = lazy(() => import("./components/ContentStudio"));
const DashboardMotor    = lazy(() => import("./components/DashboardMotor"));
const WarMapDashboard   = lazy(() => import("./components/WarMapDashboard"));
const DecisionLibrary   = lazy(() => import("./components/DecisionLibrary"));
const DNAEngine         = lazy(() => import("./components/DNAEngine"));
const AlphaHypothesis   = lazy(() => import("./components/AlphaHypothesis"));
const SovereignDashboard  = lazy(() => import("./components/SovereignDashboard"));
const SimulatorDashboard  = lazy(() => import("./components/SimulatorDashboard"));
const LearningDashboard   = lazy(() => import("./components/LearningDashboard"));
const SovereigntyDashboard = lazy(() => import("./components/SovereigntyDashboard"));
const AskDalphaDashboard  = lazy(() => import("./components/AskDalphaDashboard"));
const HealthCockpit       = lazy(() => import("./components/HealthCockpit"));
const IntegrationVault    = lazy(() => import("./components/IntegrationVault"));
const ConnectionsPanel    = lazy(() => import("./components/ConnectionsPanel"));
const DalphaStudio        = lazy(() => import("./components/DalphaStudio"));
const VoiceCloning        = lazy(() => import("./components/VoiceCloning"));
const ApiAccess           = lazy(() => import("./components/ApiAccess"));
const ReverseEngineeringPanel = lazy(() => import("./components/ReverseEngineeringPanel"));
const ProdutosPanel       = lazy(() => import("./components/ProdutosPanel"));
const FacebookVideosPanel = lazy(() => import("./components/FacebookVideosPanel"));
const DAMSLibrary         = lazy(() => import("./components/DAMSLibrary"));
const BELIntelligence     = lazy(() => import("./components/BELIntelligence"));
import "./components/StoreDashboard.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = "/api";

// Currency symbols
const CURRENCY_SYMBOLS = {
  CHF: "CHF",
  EUR: "€",
  CVE: "CVE"
};

// Category icons
const CATEGORY_ICONS = {
  alimentacao: "🍽️",
  transporte: "🚗",
  moradia: "🏠",
  saude: "💊",
  familia: "👨‍👩‍👧",
  lazer: "🎮",
  trabalho: "💼",
  impostos_taxas: "📋",
  dividas: "💳",
  poupanca_investimento: "📈",
  outros: "📦"
};

// Secondary Menu Items (moved from main tabs)
const SECONDARY_MENU_ITEMS = [
  { id: "overview", label: "Visão Geral", icon: "📊" },
  { id: "investments", label: "Investimentos", icon: "📈" },
  { id: "finances", label: "Finanças", icon: "💰" },
  { id: "tasks", label: "Tarefas", icon: "✅" },
  { id: "studio", label: "Estúdio", icon: "🎬" },
  { id: "settings", label: "Configurações", icon: "⚙️" },
  { id: "memory", label: "Memória", icon: "🧠" },
  { id: "voice", label: "Voz Contínua", icon: "🎤" },
  { id: "chat", label: "Chat Simples", icon: "💬" }
];

// Logout Button
const LogoutButton = () => {
  const { logout } = useAuth();
  return (
    <button
      onClick={logout}
      data-testid="logout-btn"
      style={{
        background: 'none',
        border: '1px solid rgba(255,255,255,0.15)',
        color: 'rgba(255,255,255,0.6)',
        padding: '4px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '600',
        fontFamily: 'inherit',
        transition: 'all 0.2s',
        marginLeft: '8px',
      }}
      onMouseEnter={e => { e.target.style.color = '#fff'; e.target.style.borderColor = 'rgba(255,255,255,0.3)'; }}
      onMouseLeave={e => { e.target.style.color = 'rgba(255,255,255,0.6)'; e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }}
    >
      Logout
    </button>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Main view: warmap (default - strategic cockpit) or secondary tab
  const [mainView, setMainView] = useState("warmap");
  const [activeTab, setActiveTab] = useState("overview");
  const [showSecondaryMenu, setShowSecondaryMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);  // 'intel'|'loja'|'criacao'|'conexoes'|'mais'|null
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookStatus, setWebhookStatus] = useState(null);
  
  // Check URL params for direct navigation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'studio') {
      setMainView('secondary');
      setActiveTab('studio');
    } else if (view === 'reverse') {
      setMainView('reverse');
    } else if (view === 'produtos') {
      setMainView('produtos');
    }
  }, []);
  
  // Investment state
  const [cryptoPrices, setCryptoPrices] = useState(null);
  const [trending, setTrending] = useState([]);
  const [news, setNews] = useState([]);
  const [investLoading, setInvestLoading] = useState(false);
  const [btcAnalysis, setBtcAnalysis] = useState(null);
  const [btcLoading, setBtcLoading] = useState(false);
  
  // FREEDON_DALPHA state
  const [freedonOverview, setFreedonOverview] = useState(null);
  const [freedonAnalysis, setFreedonAnalysis] = useState(null);
  const [freedonLoading, setFreedonLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState("bitcoin");
  
  // Alert Scheduler state
  const [alertStatus, setAlertStatus] = useState(null);
  const [alertLoading, setAlertLoading] = useState(false);
  
  // Google Calendar state
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, summaryRes, transRes, tasksRes, webhookRes, alertRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/transactions/summary`),
        axios.get(`${API}/transactions?limit=20`),
        axios.get(`${API}/tasks?status=pending`),
        axios.get(`${API}/telegram/webhook-info`),
        axios.get(`${API}/alerts/status`).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setSummary(summaryRes.data);
      setTransactions(transRes.data.transactions);
      setTasks(tasksRes.data.tasks);
      setWebhookStatus(webhookRes.data);
      setAlertStatus(alertRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvestmentData = useCallback(async () => {
    setInvestLoading(true);
    try {
      // Fetch each API separately to handle failures gracefully
      const [cryptoRes, trendingRes, newsRes, freedonRes] = await Promise.allSettled([
        axios.get(`${API}/investments/crypto`, { timeout: 15000 }),
        axios.get(`${API}/investments/trending`, { timeout: 10000 }),
        axios.get(`${API}/investments/news`, { timeout: 10000 }),
        axios.get(`${API}/freedon/overview`, { timeout: 15000 })
      ]);

      if (cryptoRes.status === 'fulfilled') {
        setCryptoPrices(cryptoRes.value.data.prices || {});
      }
      if (trendingRes.status === 'fulfilled') {
        setTrending(trendingRes.value.data.trending || []);
      }
      if (newsRes.status === 'fulfilled') {
        setNews(newsRes.value.data.news || []);
      }
      if (freedonRes.status === 'fulfilled') {
        setFreedonOverview(freedonRes.value.data);
      }
    } catch (error) {
      console.error("Error fetching investment data:", error);
    } finally {
      setInvestLoading(false);
    }
  }, []);

  const fetchBtcAnalysis = async (horizon = "medio") => {
    setBtcLoading(true);
    try {
      const response = await axios.get(`${API}/investments/bitcoin?horizon=${horizon}`);
      setBtcAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching BTC analysis:", error);
    } finally {
      setBtcLoading(false);
    }
  };

  const fetchFreedonAnalysis = async (asset = "bitcoin", assetClass = "crypto") => {
    setFreedonLoading(true);
    setSelectedAsset(asset);
    try {
      const response = await axios.get(`${API}/freedon/analysis/${asset}?asset_class=${assetClass}`);
      setFreedonAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching FREEDON analysis:", error);
    } finally {
      setFreedonLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle Google Calendar OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendar_connected") === "true") {
      setActiveTab("settings");
      fetchCalendarStatus();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("calendar_error")) {
      setActiveTab("settings");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "investments") {
      fetchInvestmentData();
    }
    if (activeTab === "settings") {
      fetchCalendarStatus();
    }
  }, [activeTab, fetchInvestmentData]);

  const fetchCalendarStatus = async () => {
    setCalendarLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/status/7524308795`);
      setCalendarStatus(res.data);
    } catch (err) {
      console.error("Calendar status error:", err);
    } finally {
      setCalendarLoading(false);
    }
  };

  const connectGoogleCalendar = () => {
    if (calendarStatus?.auth_url) {
      window.location.href = calendarStatus.auth_url;
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect/7524308795`);
      fetchCalendarStatus();
    } catch (err) {
      console.error("Disconnect error:", err);
    }
  };

  const completeTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/complete`);
      fetchData();
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const setWebhook = async () => {
    try {
      const response = await axios.post(`${API}/telegram/set-webhook?webhook_url=${encodeURIComponent(webhookUrl)}`);
      alert(response.data.ok ? "Webhook configurado!" : "Erro ao configurar webhook");
      fetchData();
    } catch (error) {
      console.error("Error setting webhook:", error);
      alert("Erro ao configurar webhook");
    }
  };

  if (loading) {
    return (
      <div className="loading-container" data-testid="loading-spinner">
        <div className="loader"></div>
        <p>Carregando Dalpha Prime...</p>
      </div>
    );
  }

  // Handle secondary menu item click
  const handleSecondaryClick = (tabId) => {
    setActiveTab(tabId);
    setMainView("secondary");
    setShowSecondaryMenu(false);
  };

  // Go back to the main strategic cockpit (War Map)
  const goToStore = () => {
    setMainView("warmap");
    setShowSecondaryMenu(false);
  };

  return (
    <div className="dashboard" data-testid="dashboard">
      {/* Header - Redesigned */}
      <header className="header" data-testid="header">
        <div className="header-content">
          <div className="logo" onClick={goToStore} style={{ cursor: 'pointer' }} data-testid="brand-logo">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_668c4879-79ca-423e-b1cc-dd12ca40e1a2/artifacts/j1j0s0hk_1937.png"
              alt="DALPHA PRIME 2.0"
              className="logo-img"
            />
          </div>
          
          {/* New Top Navigation — Grouped dropdowns to prevent overflow (Feb 2026 refactor) */}
          <div className="header-nav">
            <button
              className={`header-nav-btn warmap-nav ${mainView === 'warmap' ? 'active' : ''}`}
              onClick={() => { setMainView('warmap'); setOpenDropdown(null); }}
              data-testid="nav-warmap">
              <span className="warmap-nav-dot" /> WAR MAP
            </button>

            {/* 🧠 Inteligência — 8 modules under one group */}
            <div className="secondary-menu-wrapper">
              <button
                className={`header-nav-btn dropdown-trigger ${openDropdown === 'intel' ? 'open' : ''} ${['decisions','dna','alpha','sovereign','simulator','learning','sovereignty','ask','bel-intel'].includes(mainView) ? 'active' : ''}`}
                onClick={() => setOpenDropdown(openDropdown === 'intel' ? null : 'intel')}
                data-testid="nav-group-intel">
                🧠 Inteligência {openDropdown === 'intel' ? '▲' : '▼'}
              </button>
              {openDropdown === 'intel' && (
                <div className="secondary-dropdown" data-testid="group-menu-intel">
                  {[
                    { v: 'decisions',   i: '📚', l: 'Decision Library' },
                    { v: 'dna',         i: '🧬', l: 'DNA Engine' },
                    { v: 'alpha',       i: '⚡', l: 'Alpha Score' },
                    { v: 'sovereign',   i: '👑', l: 'Sovereign' },
                    { v: 'simulator',   i: '🔮', l: 'Simulator' },
                    { v: 'learning',    i: '🧠', l: 'Learning' },
                    { v: 'sovereignty', i: '🛡', l: 'Soberania' },
                    { v: 'ask',         i: '🦅', l: 'Ask DALPHA' },
                    { v: 'bel-intel',   i: '🇨🇻', l: 'Bo é Livre · Inteligência' },
                  ].map(x => (
                    <button key={x.v}
                      className={`dropdown-item ${mainView === x.v ? 'active' : ''}`}
                      onClick={() => { setMainView(x.v); setOpenDropdown(null); }}
                      data-testid={`nav-${x.v}`}>
                      <span className="dropdown-icon">{x.i}</span>
                      <span className="dropdown-label">{x.l}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 🏪 Loja — Store + direct access to Produtos & Engenharia Reversa (restored Feb 2026) */}
            <div className="secondary-menu-wrapper">
              <button
                className={`header-nav-btn dropdown-trigger ${openDropdown === 'loja' ? 'open' : ''} ${['store','produtos','reverse','fb-videos','cockpit','dalpha_cockpit','dashboard_motor','dams'].includes(mainView) ? 'active' : ''}`}
                onClick={() => setOpenDropdown(openDropdown === 'loja' ? null : 'loja')}
                data-testid="nav-group-loja">
                🏪 Loja {openDropdown === 'loja' ? '▲' : '▼'}
              </button>
              {openDropdown === 'loja' && (
                <div className="secondary-dropdown" data-testid="group-menu-loja">
                  {[
                    { v: 'store',           i: '🏪', l: 'Painel Loja' },
                    { v: 'produtos',        i: '📦', l: 'Produtos' },
                    { v: 'fb-videos',       i: '🎥', l: 'Vídeos Facebook' },
                    { v: 'reverse',         i: '🔬', l: 'Engenharia Reversa' },
                    { v: 'dalpha_cockpit',  i: '🎯', l: 'Cockpit Dalpha' },
                    { v: 'cockpit',         i: '🏢', l: 'W6 Cockpit' },
                    { v: 'dashboard_motor', i: '📊', l: 'Dashboard Motor' },
                    { v: 'dams',            i: '📦', l: 'Biblioteca DAMS' },
                  ].map(x => (
                    <button key={x.v}
                      className={`dropdown-item ${mainView === x.v ? 'active' : ''}`}
                      onClick={() => { setMainView(x.v); setOpenDropdown(null); }}
                      data-testid={`nav-${x.v}`}>
                      <span className="dropdown-icon">{x.i}</span>
                      <span className="dropdown-label">{x.l}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 🎬 Criação — Studio + Voz (dedup: Estúdio removed from Secondary Menu) */}
            <div className="secondary-menu-wrapper">
              <button
                className={`header-nav-btn dropdown-trigger ${openDropdown === 'criacao' ? 'open' : ''} ${['dalpha-studio','voice-clone'].includes(mainView) ? 'active' : ''}`}
                onClick={() => setOpenDropdown(openDropdown === 'criacao' ? null : 'criacao')}
                data-testid="nav-group-criacao">
                🎬 Criação {openDropdown === 'criacao' ? '▲' : '▼'}
              </button>
              {openDropdown === 'criacao' && (
                <div className="secondary-dropdown" data-testid="group-menu-criacao">
                  {[
                    { v: 'dalpha-studio', i: '🎬', l: 'DALPHA Studio' },
                    { v: 'voice-clone',   i: '🎤', l: 'Voz Contínua' },
                  ].map(x => (
                    <button key={x.v}
                      className={`dropdown-item ${mainView === x.v ? 'active' : ''}`}
                      onClick={() => { setMainView(x.v); setOpenDropdown(null); }}
                      data-testid={`nav-${x.v}`}>
                      <span className="dropdown-icon">{x.i}</span>
                      <span className="dropdown-label">{x.l}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 🔗 Conexões */}
            <div className="secondary-menu-wrapper">
              <button
                className={`header-nav-btn dropdown-trigger ${openDropdown === 'conexoes' ? 'open' : ''} ${['vault','connections','api-access'].includes(mainView) ? 'active' : ''}`}
                onClick={() => setOpenDropdown(openDropdown === 'conexoes' ? null : 'conexoes')}
                data-testid="nav-group-conexoes">
                🔗 Conexões {openDropdown === 'conexoes' ? '▲' : '▼'}
              </button>
              {openDropdown === 'conexoes' && (
                <div className="secondary-dropdown" data-testid="group-menu-conexoes">
                  {[
                    { v: 'connections', i: '🔗', l: 'Ligações' },
                    { v: 'vault',       i: '🔐', l: 'Vault' },
                    { v: 'api-access',  i: '🔌', l: 'API Access' },
                  ].map(x => (
                    <button key={x.v}
                      className={`dropdown-item ${mainView === x.v ? 'active' : ''}`}
                      onClick={() => { setMainView(x.v); setOpenDropdown(null); }}
                      data-testid={`nav-${x.v}`}>
                      <span className="dropdown-icon">{x.i}</span>
                      <span className="dropdown-label">{x.l}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className={`header-nav-btn ${mainView === 'health' ? 'active' : ''}`}
              onClick={() => { setMainView('health'); setOpenDropdown(null); }}
              data-testid="nav-health">
              🩺 Saúde
            </button>

            {/* Secondary Menu Dropdown — Estúdio removed (now in Criação) */}
            <div className="secondary-menu-wrapper">
              <button 
                className={`header-nav-btn dropdown-trigger ${showSecondaryMenu ? 'open' : ''}`}
                onClick={() => setShowSecondaryMenu(!showSecondaryMenu)}
                data-testid="nav-more"
              >
                📋 Mais {showSecondaryMenu ? '▲' : '▼'}
              </button>
              
              {showSecondaryMenu && (
                <div className="secondary-dropdown" data-testid="secondary-menu">
                  {SECONDARY_MENU_ITEMS.filter(i => i.id !== 'studio').map(item => (
                    <button
                      key={item.id}
                      className={`dropdown-item ${activeTab === item.id && mainView === 'secondary' ? 'active' : ''}`}
                      onClick={() => handleSecondaryClick(item.id)}
                      data-testid={`menu-${item.id}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="header-info">
            <span className="timezone">Europa/Zurich</span>
            <span className="status-badge">
              {webhookStatus?.result?.url ? "Online" : "Offline"}
            </span>
            <LogoutButton />
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className={`main-content ${mainView === 'store' ? 'store-mode' : ''} ${mainView === 'warmap' ? 'warmap-mode' : ''} ${mainView === 'decisions' ? 'decisions-mode' : ''} ${mainView === 'dna' ? 'warmap-mode' : ''} ${mainView === 'alpha' ? 'warmap-mode' : ''} ${mainView === 'sovereign' ? 'warmap-mode' : ''} ${mainView === 'simulator' ? 'warmap-mode' : ''} ${mainView === 'learning' ? 'warmap-mode' : ''} ${mainView === 'sovereignty' ? 'warmap-mode' : ''} ${mainView === 'ask' ? 'warmap-mode' : ''} ${mainView === 'vault' ? 'warmap-mode' : ''} ${mainView === 'connections' ? 'warmap-mode' : ''} ${mainView === 'health' ? 'warmap-mode' : ''}`}>
        <Suspense fallback={
          <div style={{ padding: "3rem", textAlign: "center", opacity: 0.7 }}
               data-testid="lazy-loading">
            ⏳ A carregar módulo…
          </div>
        }>        {/* WAR MAP — Strategic Cockpit (Landing) */}
        {mainView === "warmap" && (
          <div className="warmap-fullscreen" data-testid="warmap-section">
            <WarMapDashboard />
          </div>
        )}

        {/* DECISION LIBRARY */}
        {mainView === "decisions" && (
          <div className="warmap-fullscreen" data-testid="decisions-section">
            <DecisionLibrary />
          </div>
        )}

        {/* DNA ENGINE */}
        {mainView === "dna" && (
          <div className="warmap-fullscreen" data-testid="dna-section">
            <DNAEngine />
          </div>
        )}

        {/* ALPHA SCORE + HYPOTHESIS ENGINE */}
        {mainView === "alpha" && (
          <div className="warmap-fullscreen" data-testid="alpha-section">
            <AlphaHypothesis />
          </div>
        )}

        {/* SOVEREIGN — Knowledge Graph + Trajectory + Memory */}
        {mainView === "sovereign" && (
          <div className="warmap-fullscreen" data-testid="sovereign-section">
            <SovereignDashboard />
          </div>
        )}

        {/* MARKET SIMULATOR + ASK THE GRAPH */}
        {mainView === "simulator" && (
          <div className="warmap-fullscreen" data-testid="simulator-section">
            <SimulatorDashboard />
          </div>
        )}

        {/* LEARNING COMPILER + INSTITUTIONAL MEMORY + DECISION WEIGHTS */}
        {mainView === "learning" && (
          <div className="warmap-fullscreen" data-testid="learning-section">
            <LearningDashboard />
          </div>
        )}

        {/* SOVEREIGNTY COCKPIT — A sobrevivência da inteligência */}
        {mainView === "sovereignty" && (
          <div className="warmap-fullscreen" data-testid="sovereignty-section">
            <SovereigntyDashboard />
          </div>
        )}

        {/* HEALTH COCKPIT — Fonte oficial de diagnóstico */}
        {mainView === "health" && (
          <div className="warmap-fullscreen" data-testid="health-section">
            <HealthCockpit />
          </div>
        )}

        {/* ASK THE DALPHA — O rosto do cérebro */}
        {mainView === "ask" && (
          <div className="warmap-fullscreen" data-testid="ask-section">
            <AskDalphaDashboard />
          </div>
        )}

        {/* 🛡 Integration Vault — Cofre de credenciais encriptadas */}
        {mainView === "vault" && (
          <div className="warmap-fullscreen" data-testid="vault-section">
            <IntegrationVault />
          </div>
        )}

        {/* 🔌 Connections — Multi-plataforma multi-conta (novo modelo genérico) */}
        {mainView === "connections" && (
          <div className="warmap-fullscreen" data-testid="connections-section">
            <ConnectionsPanel />
          </div>
        )}

        {/* 🎬 DALPHA Studio — Pipeline de cortes multilingue */}
        {mainView === "dalpha-studio" && (
          <div className="warmap-fullscreen" data-testid="dalpha-studio-section">
            <DalphaStudio />
          </div>
        )}

        {/* 🎤 Voice Cloning — a tua voz em qualquer língua */}
        {mainView === "voice-clone" && (
          <div className="warmap-fullscreen" data-testid="voice-clone-section">
            <VoiceCloning />
          </div>
        )}

        {/* 🔌 API Access — MCP Server + API keys */}
        {mainView === "api-access" && (
          <div className="warmap-fullscreen" data-testid="api-access-section">
            <ApiAccess />
          </div>
        )}

        {/* Store Dashboard - Main View - Full Screen */}
        {mainView === "store" && (
          <div className="store-fullscreen">
            <StoreDashboard />
          </div>
        )}

        {/* W6 Shape Cockpit */}
        {mainView === "cockpit" && (
          <div className="content-section full-width" data-testid="w6cockpit-section">
            <W6ShapeCockpit />
          </div>
        )}

        {/* Cockpit Dalpha — Centro de Controlo da Autonomia */}
        {mainView === "dalpha_cockpit" && (
          <div className="content-section full-width" data-testid="dalpha-cockpit-section">
            <CockpitDalpha />
          </div>
        )}

        {/* Dashboard Motor — Painel Visual do Motor Autonomo */}
        {mainView === "dashboard_motor" && (
          <div className="content-section full-width" data-testid="dashboard-motor-section">
            <DashboardMotor />
          </div>
        )}

        {/* 🔬 Engenharia Reversa — Restored (Feb 2026) with lazy-load safety */}
        {mainView === "reverse" && (
          <div className="warmap-fullscreen" data-testid="reverse-section">
            <ReverseEngineeringPanel />
          </div>
        )}

        {/* 📦 Produtos — Direct link into Store Products view */}
        {mainView === "produtos" && (
          <div className="warmap-fullscreen" data-testid="produtos-section">
            <ProdutosPanel />
          </div>
        )}

        {/* 🎥 Vídeos FB — dedicated discoverable view */}
        {mainView === "fb-videos" && (
          <div className="warmap-fullscreen" data-testid="fb-videos-section">
            <FacebookVideosPanel />
          </div>
        )}

        {/* 📦 DAMS Library — Asset management biblioteca visual */}
        {mainView === "dams" && (
          <div className="warmap-fullscreen" data-testid="dams-section">
            <DAMSLibrary />
          </div>
        )}

        {/* 🇨🇻 Bo é Livre Intelligence Core */}
        {mainView === "bel-intel" && (
          <div className="warmap-fullscreen" data-testid="bel-intel-section">
            <BELIntelligence />
          </div>
        )}

        {/* Secondary Views (from dropdown menu) */}
        {mainView === "secondary" && (
          <>
            {/* Back to War Map button */}
            <div className="back-nav">
              <button className="back-btn" onClick={goToStore}>
                ← Voltar ao War Map
              </button>
              <span className="current-section">
                {SECONDARY_MENU_ITEMS.find(i => i.id === activeTab)?.icon} {SECONDARY_MENU_ITEMS.find(i => i.id === activeTab)?.label}
              </span>
            </div>

        {activeTab === "memory" && (
          <div className="content-section full-width" data-testid="memory-section">
            <MemoryDashboard userId="7524308795" />
          </div>
        )}

        {activeTab === "voice" && (
          <div className="content-section" data-testid="voice-section">
            <h2 className="section-title">🎤 Conversa Contínua</h2>
            <p className="section-description">Fala sem parar - a Dalpha ouve e responde automaticamente</p>
            <VoiceChat />
          </div>
        )}

        {activeTab === "chat" && (
          <div className="content-section" data-testid="chat-section">
            <h2 className="section-title">💬 Fala com a Dalpha Prime</h2>
            <p className="section-description">Conversa diretamente com o assistente</p>
            <ChatBot />
          </div>
        )}

        {activeTab === "studio" && (
          <ContentStudio />
        )}
        
        {activeTab === "overview" && (
          <div className="overview-grid" data-testid="overview-section">
            {/* Stats Cards */}
            <div className="stats-row">
              <div className="stat-card" data-testid="stat-transactions">
                <div className="stat-icon">💳</div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.transactions || 0}</span>
                  <span className="stat-label">Transações</span>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-tasks">
                <div className="stat-icon">📋</div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.pending_tasks || 0}</span>
                  <span className="stat-label">Tarefas Pendentes</span>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-reminders">
                <div className="stat-icon">⏰</div>
                <div className="stat-info">
                  <span className="stat-value">{stats?.pending_reminders || 0}</span>
                  <span className="stat-label">Lembretes</span>
                </div>
              </div>
            </div>

            {/* Currency Summary */}
            <div className="card currency-summary" data-testid="currency-summary">
              <h3>💰 Gastos do Mês - {summary?.month}</h3>
              <div className="currency-grid">
                {Object.entries(summary?.totals || {}).map(([currency, amount]) => (
                  <div key={currency} className={`currency-card ${currency.toLowerCase()}`}>
                    <span className="currency-code">{currency}</span>
                    <span className="currency-amount">
                      {CURRENCY_SYMBOLS[currency]} {amount.toFixed(2)}
                    </span>
                    <span className="currency-flag">
                      {currency === "CHF" ? "🇨🇭" : currency === "EUR" ? "🇪🇺" : "🇨🇻"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="card" data-testid="recent-transactions">
              <h3>📝 Últimas Transações</h3>
              <div className="transactions-list">
                {transactions.slice(0, 5).map((t) => (
                  <div key={t.id} className="transaction-item">
                    <span className="transaction-icon">{CATEGORY_ICONS[t.category] || "📦"}</span>
                    <div className="transaction-info">
                      <span className="transaction-desc">{t.description}</span>
                      <span className="transaction-category">{t.category}</span>
                    </div>
                    <span className={`transaction-amount ${t.direction}`}>
                      {t.direction === "expense" ? "-" : "+"}{t.currency} {t.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <p className="empty-state">Nenhuma transação registrada</p>
                )}
              </div>
            </div>

            {/* Pending Tasks */}
            <div className="card" data-testid="pending-tasks">
              <h3>✅ Tarefas Pendentes</h3>
              <div className="tasks-list">
                {tasks.slice(0, 5).map((task) => (
                  <div key={task.id} className="task-item">
                    <button 
                      className="task-checkbox"
                      onClick={() => completeTask(task.id)}
                      data-testid={`complete-task-${task.id}`}
                    >
                      ⬜
                    </button>
                    <span className="task-text">{task.text}</span>
                    <span className={`task-priority ${task.priority}`}>
                      {task.priority === "critical" ? "🔴" : task.priority === "high" ? "🟠" : task.priority === "medium" ? "🟡" : "⚪"}
                    </span>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <p className="empty-state">Nenhuma tarefa pendente 🎉</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "investments" && (
          <div className="investments-section" data-testid="investments-section">
            {investLoading ? (
              <div className="loading-container">
                <div className="loader"></div>
                <p>Carregando dados de mercado...</p>
              </div>
            ) : (
              <>
                {/* FREEDON_DALPHA Overview */}
                <div className="card full-width freedon-overview" data-testid="freedon-overview">
                  <h3>🎯 FREEDON_DALPHA - Sistema Institucional</h3>
                  {freedonOverview && (
                    <div className="freedon-header">
                      <div className="freedon-regime">
                        <span className="freedon-label">REGIME GLOBAL</span>
                        <span className={`freedon-value regime-${freedonOverview.regime}`}>
                          {freedonOverview.regime?.toUpperCase().replace('_', ' ')}
                        </span>
                      </div>
                      <div className="freedon-scores">
                        <div className="score-item">
                          <span>Risk-On</span>
                          <span className="score-value positive">{freedonOverview.regime_details?.risk_on_score || 0}</span>
                        </div>
                        <div className="score-item">
                          <span>Risk-Off</span>
                          <span className="score-value negative">{freedonOverview.regime_details?.risk_off_score || 0}</span>
                        </div>
                        <div className="score-item">
                          <span>Fear & Greed</span>
                          <span className={`score-value ${freedonOverview.regime_details?.fear_greed < 30 ? 'negative' : freedonOverview.regime_details?.fear_greed > 70 ? 'positive' : ''}`}>
                            {freedonOverview.regime_details?.fear_greed || 50}
                          </span>
                        </div>
                      </div>
                      <div className="freedon-factors">
                        <span className={`factor crypto-${freedonOverview.regime_details?.factors?.crypto}`}>
                          Crypto: {freedonOverview.regime_details?.factors?.crypto}
                        </span>
                        <span className={`factor equities-${freedonOverview.regime_details?.factors?.equities}`}>
                          Equities: {freedonOverview.regime_details?.factors?.equities}
                        </span>
                        <span className={`factor dollar-${freedonOverview.regime_details?.factors?.dollar}`}>
                          Dollar: {freedonOverview.regime_details?.factors?.dollar}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Asset Analysis Selector */}
                  <div className="freedon-analysis-section">
                    <h4>Análise Multi-Ativos (9 Camadas)</h4>
                    <div className="asset-selector">
                      <button className={selectedAsset === 'bitcoin' ? 'active' : ''} onClick={() => fetchFreedonAnalysis('bitcoin', 'crypto')}>BTC</button>
                      <button className={selectedAsset === 'ethereum' ? 'active' : ''} onClick={() => fetchFreedonAnalysis('ethereum', 'crypto')}>ETH</button>
                      <button className={selectedAsset === 'solana' ? 'active' : ''} onClick={() => fetchFreedonAnalysis('solana', 'crypto')}>SOL</button>
                      <button className={selectedAsset === '^GSPC' ? 'active' : ''} onClick={() => fetchFreedonAnalysis('^GSPC', 'index')}>S&P500</button>
                      <button className={selectedAsset === 'GC=F' ? 'active' : ''} onClick={() => fetchFreedonAnalysis('GC=F', 'commodity')}>Ouro</button>
                    </div>
                    
                    {freedonLoading ? (
                      <div className="freedon-loading">
                        <div className="loader"></div>
                        <p>Analisando 9 camadas institucionais...</p>
                      </div>
                    ) : freedonAnalysis ? (
                      <div className="freedon-results">
                        <div className="freedon-summary-row">
                          <div className="summary-item">
                            <span className="label">Ativo</span>
                            <span className="value">{freedonAnalysis.asset?.toUpperCase()}</span>
                          </div>
                          <div className="summary-item">
                            <span className="label">Regime</span>
                            <span className={`value regime-${freedonAnalysis.regime}`}>{freedonAnalysis.regime?.replace('_', ' ')}</span>
                          </div>
                          <div className="summary-item">
                            <span className="label">Recomendação</span>
                            <span className={`value action-${freedonAnalysis.recommendation?.action}`}>
                              {freedonAnalysis.recommendation?.action?.toUpperCase().replace('_', ' ')}
                            </span>
                          </div>
                          <div className="summary-item">
                            <span className="label">Confiança</span>
                            <span className="value">{freedonAnalysis.confidence?.toUpperCase()}</span>
                          </div>
                        </div>
                        
                        {/* Layers Summary */}
                        <div className="freedon-layers">
                          <div className="layer-item">
                            <span className="layer-icon">🌍</span>
                            <span className="layer-name">Macro</span>
                            <span className={`layer-bias bias-${freedonAnalysis.macro_analysis?.bias}`}>
                              {freedonAnalysis.macro_analysis?.bias}
                            </span>
                          </div>
                          <div className="layer-item">
                            <span className="layer-icon">📊</span>
                            <span className="layer-name">Estrutura</span>
                            <span className={`layer-bias bias-${freedonAnalysis.structure_analysis?.bias}`}>
                              {freedonAnalysis.structure_analysis?.bias}
                            </span>
                          </div>
                          <div className="layer-item">
                            <span className="layer-icon">📈</span>
                            <span className="layer-name">Volume</span>
                            <span className={`layer-bias bias-${freedonAnalysis.volume_analysis?.bias}`}>
                              {freedonAnalysis.volume_analysis?.bias}
                            </span>
                          </div>
                          <div className="layer-item">
                            <span className="layer-icon">🎭</span>
                            <span className="layer-name">Fluxo</span>
                            <span className={`layer-bias bias-${freedonAnalysis.flow_analysis?.bias}`}>
                              {freedonAnalysis.flow_analysis?.bias}
                            </span>
                          </div>
                          {freedonAnalysis.onchain_analysis && (
                            <div className="layer-item">
                              <span className="layer-icon">⛓️</span>
                              <span className="layer-name">On-Chain</span>
                              <span className={`layer-bias bias-${freedonAnalysis.onchain_analysis?.bias}`}>
                                {freedonAnalysis.onchain_analysis?.bias}
                              </span>
                            </div>
                          )}
                          <div className="layer-item">
                            <span className="layer-icon">⚠️</span>
                            <span className="layer-name">Risco</span>
                            <span className={`layer-bias risk-${freedonAnalysis.risk_assessment?.risk_level}`}>
                              {freedonAnalysis.risk_assessment?.risk_level}
                            </span>
                          </div>
                        </div>
                        
                        {/* Shadow Scenarios */}
                        <div className="freedon-scenarios">
                          <h5>Shadow Mode - Cenários</h5>
                          {freedonAnalysis.shadow_scenarios?.slice(0, 3).map((scenario, idx) => (
                            <div key={idx} className={`scenario-item scenario-${scenario.type}`}>
                              <span className="scenario-type">{scenario.type}</span>
                              <span className="scenario-prob">{(scenario.probability * 100).toFixed(0)}%</span>
                              <span className="scenario-desc">{scenario.description}</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* LLM Insight */}
                        {freedonAnalysis.llm_insight && (
                          <div className="freedon-insight">
                            <h5>💭 Insight do Analista</h5>
                            <p>{freedonAnalysis.llm_insight}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="freedon-empty">Selecione um ativo para análise institucional</p>
                    )}
                  </div>
                </div>

                {/* Crypto Prices */}
                <div className="card crypto-prices" data-testid="crypto-prices">
                  <h3>💰 Criptomoedas Favoritas</h3>
                  <div className="crypto-grid">
                    {cryptoPrices && Object.entries(cryptoPrices).map(([id, data]) => {
                      const change = data?.change_24h || 0;
                      const isPositive = change >= 0;
                      return (
                        <div key={id} className={`crypto-card ${isPositive ? 'positive' : 'negative'}`}>
                          <div className="crypto-header">
                            <span className="crypto-symbol">{data?.symbol || id.toUpperCase()}</span>
                            <span className={`crypto-change ${isPositive ? 'up' : 'down'}`}>
                              {isPositive ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
                            </span>
                          </div>
                          <div className="crypto-price">
                            ${data?.price_usd?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: data?.price_usd < 1 ? 8 : 2}) || '0'}
                          </div>
                          <div className="crypto-secondary">
                            <span>CHF {data?.price_chf?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0'}</span>
                            <span>€{data?.price_eur?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0'}</span>
                          </div>
                          <div className="crypto-meta">
                            <span>Vol: ${(data?.volume_24h / 1e9)?.toFixed(2) || 0}B</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button className="btn-refresh" onClick={fetchInvestmentData} data-testid="refresh-crypto">
                    🔄 Atualizar Preços
                  </button>
                </div>

                {/* Trending Cryptos */}
                <div className="card" data-testid="trending-cryptos">
                  <h3>🔥 Em Tendência</h3>
                  <div className="trending-list">
                    {trending.slice(0, 8).map((coin, idx) => (
                      <div key={coin.id || idx} className="trending-item">
                        <span className="trending-rank">#{idx + 1}</span>
                        <div className="trending-info">
                          <span className="trending-name">{coin.name}</span>
                          <span className="trending-symbol">{coin.symbol?.toUpperCase()}</span>
                        </div>
                        <span className="trending-mcrank">Rank #{coin.market_cap_rank || '?'}</span>
                      </div>
                    ))}
                    {trending.length === 0 && (
                      <p className="empty-state">Nenhuma tendência disponível</p>
                    )}
                  </div>
                </div>

                {/* Price Chart Section */}
                <div className="card full-width" data-testid="price-chart-section">
                  <h3>📈 Gráficos de Preços</h3>
                  <PriceChart 
                    assets={['bitcoin', 'ethereum', 'solana']} 
                    period="7d" 
                    chartType="area"
                  />
                </div>

                {/* Financial News */}
                <div className="card full-width" data-testid="financial-news">
                  <h3>📰 Notícias Financeiras</h3>
                  <div className="news-list">
                    {news.slice(0, 10).map((article, idx) => (
                      <a 
                        key={idx} 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="news-item"
                      >
                        <span className="news-source">{article.source?.replace('_', ' ').toUpperCase()}</span>
                        <span className="news-title">{article.title}</span>
                      </a>
                    ))}
                    {news.length === 0 && (
                      <p className="empty-state">Nenhuma notícia disponível</p>
                    )}
                  </div>
                </div>

                {/* Bitcoin Institutional Analysis */}
                <div className="card full-width btc-analysis" data-testid="btc-analysis">
                  <h3>₿ Análise Institucional Bitcoin</h3>
                  <div className="btc-horizon-buttons">
                    <button 
                      className="btn-horizon"
                      onClick={() => fetchBtcAnalysis('curto')}
                      data-testid="btc-short"
                    >
                      Curto Prazo
                    </button>
                    <button 
                      className="btn-horizon active"
                      onClick={() => fetchBtcAnalysis('medio')}
                      data-testid="btc-medium"
                    >
                      Médio Prazo
                    </button>
                    <button 
                      className="btn-horizon"
                      onClick={() => fetchBtcAnalysis('longo')}
                      data-testid="btc-long"
                    >
                      Ciclo
                    </button>
                  </div>
                  
                  {btcLoading ? (
                    <div className="btc-loading">
                      <div className="loader"></div>
                      <p>Analisando 6 camadas...</p>
                    </div>
                  ) : btcAnalysis ? (
                    <div className="btc-results">
                      {/* Regime e Bias */}
                      <div className="btc-header-row">
                        <div className="btc-regime">
                          <span className="btc-label">Regime</span>
                          <span className={`btc-value regime-${btcAnalysis.regime}`}>
                            {btcAnalysis.regime?.toUpperCase().replace('_', ' ')}
                          </span>
                        </div>
                        <div className="btc-bias">
                          <span className="btc-label">Viés</span>
                          <span className={`btc-value bias-${btcAnalysis.overall_bias}`}>
                            {btcAnalysis.overall_bias?.toUpperCase()}
                          </span>
                        </div>
                        <div className="btc-confidence">
                          <span className="btc-label">Confiança</span>
                          <span className="btc-value">
                            {(btcAnalysis.confidence_level * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>

                      {/* Layers */}
                      <div className="btc-layers">
                        <h4>Análise por Camada</h4>
                        {btcAnalysis.layers?.map((layer, idx) => (
                          <div key={idx} className={`btc-layer layer-${layer.bias}`}>
                            <div className="layer-header">
                              <span className={`layer-bias bias-${layer.bias}`}>
                                {layer.bias === 'bullish' ? '🟢' : layer.bias === 'bearish' ? '🔴' : '⚪'}
                              </span>
                              <span className="layer-name">{layer.name}</span>
                              <span className="layer-confidence">{(layer.confidence * 100).toFixed(0)}%</span>
                            </div>
                            {layer.key_points?.[0] && (
                              <p className="layer-point">{layer.key_points[0]}</p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Confluences & Risks */}
                      <div className="btc-confluence-risks">
                        <div className="btc-confluences">
                          <h4>Confluências</h4>
                          {btcAnalysis.confluences?.map((conf, idx) => (
                            <p key={idx} className="confluence-item">{conf}</p>
                          ))}
                        </div>
                        <div className="btc-risks">
                          <h4>Riscos</h4>
                          {btcAnalysis.risks?.map((risk, idx) => (
                            <p key={idx} className="risk-item">{risk}</p>
                          ))}
                        </div>
                      </div>

                      {/* Scenarios */}
                      <div className="btc-scenarios">
                        <h4>Cenários</h4>
                        <div className="scenario bullish">
                          <span className="scenario-icon">🟢</span>
                          <span className="scenario-text">{btcAnalysis.scenarios?.bullish}</span>
                        </div>
                        <div className="scenario bearish">
                          <span className="scenario-icon">🔴</span>
                          <span className="scenario-text">{btcAnalysis.scenarios?.bearish}</span>
                        </div>
                        <div className="scenario neutral">
                          <span className="scenario-icon">⚪</span>
                          <span className="scenario-text">{btcAnalysis.scenarios?.neutral}</span>
                        </div>
                      </div>

                      {/* Conclusion */}
                      <div className="btc-conclusion">
                        <h4>Conclusão</h4>
                        <p>{btcAnalysis.conclusion}</p>
                      </div>

                      {/* LLM Insight */}
                      {btcAnalysis.llm_insight && (
                        <div className="btc-insight">
                          <h4>💡 Insight do Analista</h4>
                          <p>{btcAnalysis.llm_insight}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="btc-empty">
                      <p>Clique em um horizonte temporal para iniciar a análise</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "finances" && (
          <div className="finances-section" data-testid="finances-section">
            {/* Category Breakdown */}
            <div className="card" data-testid="category-breakdown">
              <h3>📊 Gastos por Categoria</h3>
              <div className="category-list">
                {Object.entries(summary?.by_category || {})
                  .sort((a, b) => {
                    const totalA = Object.values(a[1]).reduce((s, v) => s + v, 0);
                    const totalB = Object.values(b[1]).reduce((s, v) => s + v, 0);
                    return totalB - totalA;
                  })
                  .map(([category, amounts]) => {
                    const total = Object.values(amounts).reduce((s, v) => s + v, 0);
                    if (total === 0) return null;
                    return (
                      <div key={category} className="category-item">
                        <div className="category-header">
                          <span className="category-icon">{CATEGORY_ICONS[category] || "📦"}</span>
                          <span className="category-name">{category}</span>
                        </div>
                        <div className="category-amounts">
                          {Object.entries(amounts).map(([curr, amt]) => 
                            amt > 0 && (
                              <span key={curr} className={`amount-badge ${curr.toLowerCase()}`}>
                                {curr} {amt.toFixed(2)}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* All Transactions */}
            <div className="card full-width" data-testid="all-transactions">
              <h3>📝 Todas as Transações</h3>
              <div className="transactions-table">
                <table>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th>Categoria</th>
                      <th>Moeda</th>
                      <th>Valor</th>
                      <th>País</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.id}>
                        <td>{new Date(t.datetime).toLocaleDateString('pt-PT')}</td>
                        <td>{t.description}</td>
                        <td>{CATEGORY_ICONS[t.category]} {t.category}</td>
                        <td>{t.currency}</td>
                        <td className={t.direction}>{t.direction === "expense" ? "-" : "+"}{t.amount.toFixed(2)}</td>
                        <td>{t.country === "CH" ? "🇨🇭" : t.country === "PT" ? "🇵🇹" : "🇨🇻"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactions.length === 0 && (
                  <p className="empty-state">Nenhuma transação registrada</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "tasks" && (
          <div className="tasks-section" data-testid="tasks-section">
            <div className="card full-width">
              <h3>📋 Todas as Tarefas</h3>
              <div className="tasks-full-list">
                {tasks.map((task) => (
                  <div key={task.id} className="task-full-item">
                    <button 
                      className="task-checkbox-large"
                      onClick={() => completeTask(task.id)}
                      data-testid={`complete-task-full-${task.id}`}
                    >
                      ✓
                    </button>
                    <div className="task-details">
                      <span className="task-text-large">{task.text}</span>
                      <div className="task-meta">
                        <span className="task-date">
                          Criada: {new Date(task.created_at).toLocaleDateString('pt-PT')}
                        </span>
                        {task.due_date && (
                          <span className="task-due">
                            Prazo: {new Date(task.due_date).toLocaleDateString('pt-PT')}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`task-priority-badge ${task.priority}`}>
                      {task.priority}
                    </span>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <div className="empty-state-large">
                    <span className="empty-icon">🎉</span>
                    <p>Nenhuma tarefa pendente!</p>
                    <p className="empty-hint">Use /tarefa no Telegram para criar tarefas</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="settings-section" data-testid="settings-section">
            {/* Webhook Configuration */}
            <div className="card" data-testid="webhook-config">
              <h3>🔗 Configuração do Webhook</h3>
              <div className="webhook-status">
                <p>
                  <strong>Status:</strong>{" "}
                  {webhookStatus?.result?.url ? (
                    <span className="status-online">✅ Ativo</span>
                  ) : (
                    <span className="status-offline">⚠️ Não configurado</span>
                  )}
                </p>
                {webhookStatus?.result?.url && (
                  <p><strong>URL atual:</strong> {webhookStatus.result.url}</p>
                )}
              </div>
              <div className="webhook-form">
                <input 
                  type="text"
                  placeholder={`${BACKEND_URL}/api/telegram/webhook`}
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="webhook-input"
                  data-testid="webhook-url-input"
                />
                <button 
                  onClick={setWebhook}
                  className="btn-primary"
                  data-testid="set-webhook-btn"
                >
                  Configurar Webhook
                </button>
              </div>
              <p className="hint">
                Use: <code>{BACKEND_URL}/api/telegram/webhook</code>
              </p>
            </div>

            {/* Google Calendar Integration */}
            <div className="card" data-testid="google-calendar-card" style={{
              background: calendarStatus?.connected
                ? "linear-gradient(135deg, rgba(15,157,88,0.08) 0%, rgba(66,133,244,0.06) 100%)"
                : "rgba(255,255,255,0.02)",
              border: calendarStatus?.connected
                ? "1px solid rgba(15,157,88,0.3)"
                : "1px solid rgba(255,255,255,0.08)"
            }}>
              <h3 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "20px" }}>📅</span>
                Google Calendar
                {calendarStatus?.connected && (
                  <span style={{
                    fontSize: "10px", fontWeight: 700, letterSpacing: "1px",
                    color: "#0F9D58", background: "rgba(15,157,88,0.12)",
                    border: "1px solid rgba(15,157,88,0.3)",
                    borderRadius: "4px", padding: "2px 8px"
                  }}>CONECTADO</span>
                )}
              </h3>
              {calendarLoading ? (
                <p style={{ color: "#888", fontSize: "13px" }}>A verificar estado...</p>
              ) : calendarStatus?.connected ? (
                <div>
                  <p style={{ color: "#0F9D58", fontSize: "13px", marginBottom: "12px" }}>
                    ✅ Google Calendar conectado com sucesso! A Dalpha Prime pode agora ler e criar eventos.
                  </p>
                  <button
                    onClick={disconnectGoogleCalendar}
                    data-testid="calendar-disconnect-btn"
                    style={{
                      background: "rgba(255,60,60,0.1)", border: "1px solid rgba(255,60,60,0.3)",
                      color: "#ff6b6b", borderRadius: "8px", padding: "8px 16px",
                      cursor: "pointer", fontSize: "12px"
                    }}
                  >
                    Desligar Calendar
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ color: "#aaa", fontSize: "13px", marginBottom: "12px" }}>
                    Liga o teu Google Calendar para que a Dalpha Prime possa gerir os teus eventos, criar reuniões e verificar a tua disponibilidade.
                  </p>
                  {!calendarStatus?.configured ? (
                    <p style={{ color: "#ff9900", fontSize: "12px" }}>
                      ⚠️ Credenciais Google não configuradas no servidor.
                    </p>
                  ) : (
                    <div>
                      <button
                        onClick={connectGoogleCalendar}
                        data-testid="calendar-connect-btn"
                        style={{
                          background: "linear-gradient(135deg, #4285F4 0%, #0F9D58 100%)",
                          border: "none", color: "white", borderRadius: "8px",
                          padding: "10px 20px", cursor: "pointer", fontSize: "13px",
                          fontWeight: 600, display: "flex", alignItems: "center", gap: "8px"
                        }}
                      >
                        <span>📅</span> Ligar Google Calendar
                      </button>
                      <p style={{ color: "#666", fontSize: "11px", marginTop: "8px" }}>
                        Redireciona para autenticação Google → volta automaticamente
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Profile */}
            <div className="card" data-testid="user-profile">
              <h3>👤 Perfil do Usuário</h3>
              <div className="profile-grid">
                <div className="profile-item">
                  <span className="profile-icon">🌍</span>
                  <div className="profile-info">
                    <span className="profile-label">Localização</span>
                    <span className="profile-value">Suíça 🇨🇭</span>
                  </div>
                </div>
                <div className="profile-item">
                  <span className="profile-icon">🕐</span>
                  <div className="profile-info">
                    <span className="profile-label">Fuso Horário</span>
                    <span className="profile-value">Europa/Zurich</span>
                  </div>
                </div>
                <div className="profile-item">
                  <span className="profile-icon">😴</span>
                  <div className="profile-info">
                    <span className="profile-label">Horário de Sono</span>
                    <span className="profile-value">23:00 - 04:45</span>
                  </div>
                </div>
                <div className="profile-item">
                  <span className="profile-icon">💰</span>
                  <div className="profile-info">
                    <span className="profile-label">Moedas</span>
                    <span className="profile-value">CHF, EUR, CVE</span>
                  </div>
                </div>
                <div className="profile-item">
                  <span className="profile-icon">🏠</span>
                  <div className="profile-info">
                    <span className="profile-label">Origem</span>
                    <span className="profile-value">Cabo Verde 🇨🇻</span>
                  </div>
                </div>
                <div className="profile-item">
                  <span className="profile-icon">🪪</span>
                  <div className="profile-info">
                    <span className="profile-label">Cidadania</span>
                    <span className="profile-value">Portugal 🇵🇹</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alert Scheduler Status */}
            <div className="card alert-scheduler-card" data-testid="alert-scheduler">
              <h3>🔔 Sistema de Alertas Automáticos</h3>
              {alertStatus ? (
                <div className="alert-status">
                  <div className="alert-status-row">
                    <div className="status-item">
                      <span className="status-label">Status</span>
                      <span className={`status-value ${alertStatus.running ? 'active' : 'inactive'}`}>
                        {alertStatus.running ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Regime Atual</span>
                      <span className="status-value regime">{alertStatus.last_regime || 'N/A'}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Inscritos</span>
                      <span className="status-value">{alertStatus.chat_ids_count}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Alertas Hoje</span>
                      <span className="status-value">{alertStatus.alerts_sent_today}</span>
                    </div>
                  </div>
                  
                  <div className="alert-thresholds">
                    <h4>Configurações de Alerta</h4>
                    <div className="threshold-grid">
                      <div className="threshold-item">
                        <span className="threshold-icon">📊</span>
                        <span className="threshold-label">Movimento BTC</span>
                        <span className="threshold-value">{alertStatus.thresholds?.btc_movement}</span>
                      </div>
                      <div className="threshold-item">
                        <span className="threshold-icon">😨</span>
                        <span className="threshold-label">Fear & Greed Baixo</span>
                        <span className="threshold-value">&lt;{alertStatus.thresholds?.fear_greed_low}</span>
                      </div>
                      <div className="threshold-item">
                        <span className="threshold-icon">🤑</span>
                        <span className="threshold-label">Fear & Greed Alto</span>
                        <span className="threshold-value">&gt;{alertStatus.thresholds?.fear_greed_high}</span>
                      </div>
                      <div className="threshold-item">
                        <span className="threshold-icon">⏰</span>
                        <span className="threshold-label">Intervalo</span>
                        <span className="threshold-value">{alertStatus.check_interval_minutes} min</span>
                      </div>
                      <div className="threshold-item">
                        <span className="threshold-icon">🌙</span>
                        <span className="threshold-label">Silêncio</span>
                        <span className="threshold-value">{alertStatus.quiet_hours}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="alert-info">
                    <p>
                      <strong>Como ativar:</strong> Envie <code>/subscribe</code> no Telegram para receber alertas automáticos.
                    </p>
                    <p>
                      O sistema monitora mudanças de regime, Fear & Greed em níveis extremos, e movimentos significativos do BTC.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="alert-unavailable">
                  <p>Sistema de alertas não disponível</p>
                </div>
              )}
            </div>

            {/* Commands Reference */}
            <div className="card full-width" data-testid="commands-reference">
              <h3>📚 Comandos do Bot</h3>
              <div className="commands-grid">
                <div className="command-group">
                  <h4>📋 Gerais</h4>
                  <ul>
                    <li><code>/ajuda</code> - Mostrar ajuda</li>
                    <li><code>/rapido</code> - Modo rápido</li>
                    <li><code>/profundo</code> - Modo investigação</li>
                  </ul>
                </div>
                <div className="command-group">
                  <h4>📅 Planejamento</h4>
                  <ul>
                    <li><code>/hoje</code> - Resumo do dia</li>
                    <li><code>/tarefa [texto]</code> - Nova tarefa</li>
                    <li><code>/prioridades</code> - Ver prioridades</li>
                    <li><code>/lembrete [hora] [msg]</code> - Criar lembrete</li>
                  </ul>
                </div>
                <div className="command-group">
                  <h4>💰 Finanças</h4>
                  <ul>
                    <li><code>/fin add [valor] [moeda] [cat] [desc]</code></li>
                    <li><code>/fin recibo</code> - Enviar foto</li>
                    <li><code>/fin resumo [mes]</code></li>
                    <li><code>/fin query [pergunta]</code></li>
                  </ul>
                </div>
                <div className="command-group">
                  <h4>📈 Investimentos</h4>
                  <ul>
                    <li><code>/preco [ativo]</code> - Preço atual</li>
                    <li><code>/cripto</code> - Preços favoritas</li>
                    <li><code>/mercado</code> - Visão geral</li>
                    <li><code>/insights</code> - Análise e oportunidades</li>
                    <li><code>/trending</code> - Criptos em tendência</li>
                    <li><code>/noticias</code> - Notícias financeiras</li>
                    <li><code>&quot;vou dormir&quot;</code> - Análise noturna</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
          </>
        )}
        </Suspense>
      </main>

      {/* Footer - hidden in store mode */}
      {mainView !== "store" && (
        <footer className="footer" data-testid="footer">
          <p>Dalpha Prime - Seu Agente Autônomo Pessoal</p>
        </footer>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="dalfa-loading-screen">
        <div className="dalfa-loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  return <Dashboard />;
}

export default App;
